// Starting-package engine — grants a paid role's one-time head start to the
// ACTIVE kingdom: a treasury top-up, citizens, and a pre-built/developed
// kingdom (zoned land + buildings). Free roles have no package and no-op.
//
// Run this right AFTER completeOnboarding() (which sets the active kingdom), so
// the default starter (home @ 0,0 + one rice paddy @ 1,0 from save.js) already
// exists and we only add on top. Building placement honors each building's
// BuildingConfig.allowedPlotTypes: Temple→home, Granary→a rice paddy, and
// School/Hospital/Watchtower→fresh COMMERCIAL tiles zoned here.

import { Roles, BuildingConfig, PlotTypes } from '../config/balance.js'
import { active, persist } from './state.js'
import { earnWealth, adjustGoonsOwned } from './seasonManager.js'
import { addBuilding } from './buildingState.js'
import { key, fieldCoords, hexDistance } from '../scene/hexGrid.js'

const HOME = { q: 0, r: 0 }

export function applyStartingPackage(roleId) {
  const pkg = Roles[roleId]?.startingPackage
  if (!pkg) return // free roles → default starter only

  const k = active()
  if (!k.plots) k.plots = {}

  // 1) Treasury (emits WealthChange + persists).
  if (pkg.wealth > 0) earnWealth(pkg.wealth)

  // 2) Citizens (each emits GoonOwnedChange + persists).
  for (const [type, n] of Object.entries(pkg.goons ?? {})) {
    if (n > 0) adjustGoonsOwned(type, n)
  }

  // 3) Free in-field tiles, nearest-to-home first, deterministic — for new land.
  const owned = new Set(Object.keys(k.plots))
  const pool = fieldCoords()
    .filter((c) => !(c.q === HOME.q && c.r === HOME.r) && !owned.has(key(c.q, c.r)))
    .map((c) => ({ k: key(c.q, c.r), d: hexDistance(c, HOME), q: c.q, r: c.r }))
    .sort((a, b) => a.d - b.d || a.q - b.q || a.r - b.r)
    .map((c) => c.k)
  let pi = 0
  const zoneNewTile = (type) => {
    const t = pool[pi++]
    if (!t) return null
    k.plots[t] = { type }
    return t
  }

  // 4) Extra land (so the granary has a paddy + spare residential to expand on).
  for (let i = 0; i < (pkg.extraFarms ?? 0); i++) zoneNewTile(PlotTypes.FARM)
  for (let i = 0; i < (pkg.extraResidential ?? 0); i++) zoneNewTile(PlotTypes.RESIDENTIAL)

  // 5) Buildings — one per correctly-zoned tile (never two on the same tile).
  const used = new Set((k.buildings ?? []).map((b) => String(b.tileIndex)))
  const ownedTileOfType = (type) =>
    Object.keys(k.plots).find((t) => k.plots[t]?.type === type && !used.has(t))

  for (const type of pkg.buildings ?? []) {
    const allowed = BuildingConfig[type]?.allowedPlotTypes ?? []
    let tile = null
    if (allowed.includes(PlotTypes.HOME)) tile = ownedTileOfType(PlotTypes.HOME)            // Temple
    else if (allowed.includes(PlotTypes.FARM)) tile = ownedTileOfType(PlotTypes.FARM)        // Granary
    else if (allowed.includes(PlotTypes.COMMERCIAL)) tile = zoneNewTile(PlotTypes.COMMERCIAL) // School/Hospital/Watchtower
    else if (allowed.includes(PlotTypes.RESIDENTIAL)) tile = ownedTileOfType(PlotTypes.RESIDENTIAL)
    if (tile) { addBuilding(type, tile); used.add(tile) }
  }

  persist()
}
